import { useMemo } from 'react';
import { useCurrentSubscription } from 'src/store/subscription';
import { useJourneys } from 'src/store/journeys';
import {
  isFeatureAllowed,
  getFeatureUpgradeMessage,
  getFeatureRequiredPlan,
  needsUpgradeForFeature,
  isFeatureGroupRestricted,
  FEATURE_RESTRICTIONS,
  FEATURE_GROUPS
} from 'src/utils/feature-restrictions';

export const useFeatureRestrictions = () => {
  const {
    subscriptionPlanType,
    isOnTrial,
    isSubscribed,
    setOpenPricingPaywall,
    setCustomMsg,
    setCustomHeader,
    setFeature
  } = useCurrentSubscription();

  // Get journey limit information
  const hasReachedFreeLimit = useJourneys((state) => state.hasReachedFreeLimit);

  const checkFeature = useMemo(() => {
    return (feature: string) => {
      return isFeatureAllowed(feature, subscriptionPlanType, isOnTrial, hasReachedFreeLimit);
    };
  }, [subscriptionPlanType, isOnTrial, hasReachedFreeLimit]);

  const getUpgradeMessage = useMemo(() => {
    return (feature: string) => {
      return getFeatureUpgradeMessage(feature, subscriptionPlanType);
    };
  }, [subscriptionPlanType]);

  const getRequiredPlan = useMemo(() => {
    return (feature: string) => {
      return getFeatureRequiredPlan(feature);
    };
  }, []);

  const needsUpgrade = useMemo(() => {
    return (feature: string) => {
      return needsUpgradeForFeature(feature, subscriptionPlanType, isOnTrial, hasReachedFreeLimit);
    };
  }, [subscriptionPlanType, isOnTrial, hasReachedFreeLimit]);

  const checkFeatureGroup = useMemo(() => {
    return (featureGroup: string[]) => {
      return isFeatureGroupRestricted(featureGroup, subscriptionPlanType, isOnTrial);
    };
  }, [subscriptionPlanType, isOnTrial]);

  const showUpgradeModal = useMemo(() => {
    return (feature: string, customMessage?: string, customHeader?: string) => {
      const message = customMessage || getFeatureUpgradeMessage(feature, subscriptionPlanType);
      const header = customHeader || 'Upgrade Required';
      
      if (message) {
        setCustomMsg(message);
        setCustomHeader(header);
        setFeature(feature);
        setOpenPricingPaywall(true);
      }
    };
  }, [subscriptionPlanType]);

  // Predefined feature checks for common features
  const features = useMemo(() => ({
    // Analytics features
    canAccessBasicInsights: checkFeature('basic_insights'),
    canAccessAdvancedInsights: checkFeature('advanced_insights'),
    canAccessSessionReplay: checkFeature('session_replay'),
    
    // Workspace features
    canAccessWorkspaces: checkFeature('workspaces'),
    canAccessOrganizationTemplates: checkFeature('organization_templates'),
    canAccessTagsAndFilters: checkFeature('tags_and_filters'),
    
    // Integration features
    canAccessClayIntegration: checkFeature('clay_integration'),
    canAccessOtherIntegrations: checkFeature('other_integrations'),
    canAccessSlackNotifications: checkFeature('slack_notifications'),
    canAccessGoogleDriveIntegration: checkFeature('google_drive_integration'),
    canAccessZapierIntegration: checkFeature('zapier_integration'),
    
    // Customization features
    canCustomizeNDA: checkFeature('customize_nda'),
    canRequireNDA: checkFeature('nda_required'),
    canAccessEmailTracking: checkFeature('email_tracking'),
    canRemoveBranding: checkFeature('made_in_journey_branding'),
    canCustomizeMeta: checkFeature('customize_meta'),
    canAccessGoogleIndexing: checkFeature('google_indexing'),
    canAccessCustomFonts: checkFeature('custom_fonts'),
    
    // AI features
    canAccessAIChatbot: checkFeature('ai_chatbot'),
    canCreateWithAI: checkFeature('create_with_ai'),
    
    // Basic features (available on all plans)
    canAccessJourneyModes: checkFeature('journey_modes'),
    canAccessContentBlocks: checkFeature('content_blocks'),
    canAccessContentLibrary: checkFeature('content_library'),
    canAccessSyncedSteps: checkFeature('synced_steps'),
    canAccessEmailNotifications: checkFeature('email_notifications'),
    canAccessComments: checkFeature('comments'),
    canAccessAccessPermissions: checkFeature('access_permissions'),
    canAccessUserRoles: checkFeature('user_roles'),
    
    // Journey limits
    hasUnlimitedJourneys: checkFeature('unlimited_journeys'),
    canCreateJourney: checkFeature('journey_creation'),
    
    // Branding
    hasBranding: checkFeature('made_in_journey_branding')
  }), [checkFeature]);

  // Feature groups
  const featureGroups = useMemo(() => ({
    analytics: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.ANALYTICS),
      features: {
        basicInsights: features.canAccessBasicInsights,
        advancedInsights: features.canAccessAdvancedInsights,
        sessionReplay: features.canAccessSessionReplay
      }
    },
    workspace: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.WORKSPACE),
      features: {
        workspaces: features.canAccessWorkspaces,
        organizationTemplates: features.canAccessOrganizationTemplates,
        tagsAndFilters: features.canAccessTagsAndFilters
      }
    },
    integrations: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.INTEGRATIONS),
      features: {
        clayIntegration: features.canAccessClayIntegration,
        otherIntegrations: features.canAccessOtherIntegrations,
        slackNotifications: features.canAccessSlackNotifications,
        googleDriveIntegration: features.canAccessGoogleDriveIntegration,
        zapierIntegration: features.canAccessZapierIntegration
      }
    },
    customization: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.CUSTOMIZATION),
      features: {
        customizeNDA: features.canCustomizeNDA,
        emailTracking: features.canAccessEmailTracking,
        removeBranding: features.canRemoveBranding,
        customizeMeta: features.canCustomizeMeta,
        googleIndexing: features.canAccessGoogleIndexing,
        customFonts: features.canAccessCustomFonts
      }
    },
    aiFeatures: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.AI_FEATURES),
      features: {
        aiChatbot: features.canAccessAIChatbot,
        createWithAI: features.canCreateWithAI
      }
    },
    journeyFeatures: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.JOURNEY_FEATURES),
      features: {
        unlimitedJourneys: features.hasUnlimitedJourneys,
        journeyModes: features.canAccessJourneyModes,
        contentBlocks: features.canAccessContentBlocks,
        contentLibrary: features.canAccessContentLibrary,
        syncedSteps: features.canAccessSyncedSteps
      }
    },
    collaboration: {
      isRestricted: checkFeatureGroup(FEATURE_GROUPS.COLLABORATION),
      features: {
        comments: features.canAccessComments,
        accessPermissions: features.canAccessAccessPermissions,
        userRoles: features.canAccessUserRoles,
        emailNotifications: features.canAccessEmailNotifications
      }
    }
  }), [checkFeatureGroup, features]);

  return {
    // Core functions
    checkFeature,
    getUpgradeMessage,
    getRequiredPlan,
    needsUpgrade,
    checkFeatureGroup,
    showUpgradeModal,
    
    // Subscription state
    subscriptionPlanType,
    isOnTrial,
    isSubscribed,
    
    // Predefined feature checks
    features,
    
    // Feature groups
    featureGroups,
    
    // Available features for current plan
    availableFeatures: Object.keys(FEATURE_RESTRICTIONS).filter(feature => 
      checkFeature(feature)
    ),
    
    // Restricted features for current plan
    restrictedFeatures: Object.keys(FEATURE_RESTRICTIONS).filter(feature => 
      needsUpgrade(feature)
    )
  };
}; 